import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, ModalSize, NotificationToaster } from "components/notifications";
import { GridLayout } from "components/grid";
import { DataDisplayRecord } from "components/data-display";
import { Button, ButtonSave } from "components/buttons";
import { FormRadio } from "components/form-fields";
import { LicenceCheckingService } from "services";
import { Intent } from "@blueprintjs/core";
import { ShowHide } from "components/layout";
import PropTypes from "prop-types";

export function LicenceCheckingLicenceConflictModal({
    isOpen,
    onClose,
    employeeName = '',
    employeeId = '',
    emailAddress = '',
    onSuccess,
    consentId = null,
    consentLicenceNumber = '',
    existingLicenceNumber = '',
    licenceNumberOverridable
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const restartDisabled = emailAddress == null || emailAddress === "";
    const modalActions = [
        { value: "approve", label: "Approve this declaration" },
        {
            value: "restart",
            label: "Resend new driver declaration",
            disabled: restartDisabled,
            tooltip: restartDisabled ? "This driver has no email address on file." : undefined
        },
        { value: "onsite", label: "Perform on-site declaration" }
    ];
    const [selectedAction, setSelectedAction] = useState(null);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [updateLicenceNumberAction, setUpdateLicenceNumberAction] = useState(null);

    function onActionChange(value) {
        setSelectedAction(value.target.value);
    }

    function onUpdateLicenceNumberChange(value) {
        setUpdateLicenceNumberAction(value.currentTarget.value === "true");
    }

    function onConfirm() {
        if (selectedAction === "onsite") {
            onClose();
            navigate(`/licence-checking/declaration/on-site/${employeeId}`, {
                state: {
                    returnTo: location.pathname,
                    cancelTo: location.pathname,
                },
            });
            return;
        }

        setActionInProgress(true);

        if(selectedAction === "approve") {
            const updateLicenceNumberDecision = {
                updateEmployeeLicenceNumber: updateLicenceNumberAction == null ? false : updateLicenceNumberAction
            }

            LicenceCheckingService.confirmLicenceMismatch(consentId, updateLicenceNumberDecision).then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Driver's declaration has been successfully processed.");
                onSuccess();
                onClose();
                setActionInProgress(false);
            }, function() {
                setActionInProgress(false);
                NotificationToaster.show(Intent.DANGER, "Failed to approve the licence declaration. Please try again.");
            });
        } else {

            const consentRequest = {
                employeeId: employeeId,
                emailAddress: emailAddress,
                updateEmployeeEmail: false,
            }

            LicenceCheckingService.sendDriverDeclaration(consentRequest).then(() => {
                NotificationToaster.show(Intent.SUCCESS, "A new driver declaration has been sent.");
                onSuccess();
                onClose();
                setActionInProgress(false);
            }, function() {
                setActionInProgress(false);
                NotificationToaster.show(Intent.DANGER, "Failed to create a new declaration. Please try again.");
            });
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Conflicting Details"}
            size={ModalSize.MEDIUM}
            clickOutsideClose={false}
            showCloseButton={false}
        >
            <div className="spacer-bottom">
                <p>There are conflicting details between {employeeName}'s licence number given on the driver declaration and the licence number held on the Vision profile. Do you want to approve the current consent and proceed with the new licence number or do you want to restart the driver declaration process? Approving this declaration will not update Vision's existing record.</p>
            </div>
            <div className="spacer-bottom">
                <GridLayout numberOfColumns={2}>
                    <DataDisplayRecord header="Driver Declaration Licence Number" data={consentLicenceNumber} />
                    <DataDisplayRecord header="Vision Licence Number" data={existingLicenceNumber} />
                </GridLayout>
            </div>
            <FormRadio
                headingText="Please select the action you would like to take:"
                options={modalActions}
                selectedValue={selectedAction}
                onChange={onActionChange}
                disabled={actionInProgress}
            />
            <ShowHide
                evaluator={licenceNumberOverridable && selectedAction === "approve"}
                show={
                    <FormRadio
                        headingText="Would you like to update the Vision licence number to match the declaration licence number?"
                        selectedValue={updateLicenceNumberAction}
                        onChange={onUpdateLicenceNumberChange}
                        disabled={actionInProgress}
                    />
                }
            />
            <div className="inline-items inline-items-end">
                <Button text="Cancel" intent="Secondary" onClick={onClose} disabled={actionInProgress}></Button>
                <ButtonSave simpleDisabled={selectedAction == null || consentId == null} disabled={actionInProgress} icon={null} text="Confirm" onClick={onConfirm} />
            </div>
        </Modal >
    );
}

LicenceCheckingLicenceConflictModal.propTypes = {
    consentId: PropTypes.number,
    consentLicenceNumber: PropTypes.string,
    existingLicenceNumber: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    employeeName: PropTypes.string,
    employeeId: PropTypes.string,
    emailAddress: PropTypes.string,
    licenceNumberOverridable: PropTypes.bool.isRequired
};